import { useEffect, useRef } from "react";
import { PDFDocument } from "pdf-lib";
import fontkit from '@pdf-lib/fontkit'

import { PnlItem } from 'types';

export const checkIfInputIsEmpty = (input: any) => {
    return input === '' || input === null || input === 0 || input.length === 0;
}

export const checkIfNumberIsIntegerAndNonZero = (number: Number) => {
    return Number.isInteger(number) && number > 0;
}

export enum UserStepNames {
    start = 'register.start',
    regDocuments = 'register.documents',
    KYC = 'edit.kyc',
    bookCall = 'register.book-call',
    documents = 'documents',
    docsStart = 'docs-start',
    regKYC = 'reg_kyc',
}

export const checkUserStep = (user: any, userCore: any, history: any) => {
    if(user){
        if (user.step === UserStepNames.start) {
            return history.replace('/register/start');
        }

        if (user.step && user.step.match(/register\.step\d+/)) {
            return history.replace('/register/complete');
        }

        if (user.step === UserStepNames.regDocuments) {
            return history.replace('/register/documents');
        }

        if (user.step === UserStepNames.KYC) {
            return history.replace('/register/complete');
        }

        if (user.step === UserStepNames.bookCall) {
            return history.replace('/register/book-call');
        }

        if (user.step === UserStepNames.documents) {
            return history.replace('/documents');
        }

        if (user.step === UserStepNames.regKYC || user.step === UserStepNames.docsStart) {
            if(user.kyc_step){
                return history.replace(`/user/kyc`)
            }
        }
        if (userCore?.status == 'verified' && !userCore?.portfolio) {
            return history.replace('/register/portfolio');
        }
    }
    else {
        return history.replace(`/login`)
    }
}

export const useInterval = (callback: any, delay: number | null) => {
    const savedCallback = useRef();

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        const tick = () => {
            // @ts-ignore
            savedCallback.current();
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

export const downloadFile = (data: any, filename: any, mime: any) => {
    const blob = new Blob([data], { type: mime || 'application/octet-stream' });
    if (typeof window.navigator.msSaveBlob !== 'undefined') {
        window.navigator.msSaveBlob(blob, filename);
        return;
    }
    const blobURL = window.URL.createObjectURL(blob);
    const tempLink = document.createElement('a');
    tempLink.style.display = 'none';
    tempLink.href = blobURL;
    tempLink.setAttribute('download', filename);
    if (typeof tempLink.download === 'undefined') {
        tempLink.setAttribute('target', '_blank');
    }
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    setTimeout(() => {
        window.URL.revokeObjectURL(blobURL);
    }, 100);
}


export const loadPdfDocument = async (user: any, documentDate?: Date) => {
    let url = '/documents/loan_agreement_form.pdf';
    const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer())

    url = '/documents/FreeSans.ttf'
    const fontBytes = await fetch(url).then((res) => res.arrayBuffer());

    // Load a PDFDocument from the existing PDF bytes
    const pdfDoc = await PDFDocument.load(existingPdfBytes)
    pdfDoc.registerFontkit(fontkit);
    const freeSansFont = await pdfDoc.embedFont(fontBytes)

    const form = pdfDoc.getForm();

    const phoneField = form.getTextField('phone');
    const emailField = form.getTextField('email');
    const nameField = form.getTextField('name');
    const idField = form.getTextField('id');
    const dateField = form.getTextField('date');

    phoneField.setText(user.phone);
    phoneField.updateAppearances(freeSansFont);
    emailField.setText(user.email);
    emailField.updateAppearances(freeSansFont);
    nameField.setText(`${user.firstName} ${user.lastName}`);
    nameField.updateAppearances(freeSansFont);
    
    idField.setText(user.idNumber);
    idField.updateAppearances(freeSansFont);
    const expand = (n: number, digits: number) => {
        return "000000000".substring(0, digits - ("" + n).length) + n;
    };
    if (documentDate) {
        dateField.setText(expand(documentDate.getDate(), 2) + "/" + expand(documentDate.getMonth() + 1, 2) + "/" + expand(documentDate.getFullYear(), 4));
        dateField.updateAppearances(freeSansFont);
    }
    try {
        form.flatten();
    } catch (flattenError) {
        console.warn('Could not flatten form, continuing without flattening:', flattenError);
    }

    return await pdfDoc.save();
}

export const colorList = [
    "#3F51B5",
    "#EC557A",
    "#FF8C5A",
    "#FE9F99",
    "#ffb703",
    "#5BCEAE",
    "#0a9396",
    "#457b9d",
    "#5F6D95",
    "#e5989b",
    "#b5838d"
];

export const getPercentByTotalValue = (value: number, total: number) => Math.round( value / total * 100 );

export interface Percentage {
    sum: number;
    percent: {
        [key: number]: number,
    }
}

export const getPercentage = (indexes: PnlItem[] | undefined, totalValue: number) => indexes?.reduce((acc: Percentage, item: PnlItem, index: number) => {
    let percent;
    if (index !== (indexes.length - 1)) {
        percent = getPercentByTotalValue(item.currentValue, totalValue);
        acc.sum += percent;
        acc.percent[index] = percent;
    } else {
        percent = 100 - acc.sum;
        acc.percent[index] = percent;
    }
    return acc;
}, {
    sum: 0,
    percent: {}
})
